// Extracted from EnhancedResults.js (structural refactor, July 2026).
// Analysis-result view components — no behavior change.
import React, { useState, useRef, useCallback } from 'react';
import { Download, GitFork } from 'lucide-react';

function PathDiagram({ pathCoeffs, depVar, correlationMatrix, residualEffect, rSquared }) {
  const [hovered, setHovered] = React.useState(null);
  const svgRef = React.useRef(null);
  const vars = Object.keys(pathCoeffs);
  const n = vars.length;

  // Color palette for each variable's arrows (like the reference uses distinct colors)
  const varColors = [
    '#dc2626', '#2563eb', '#16a34a', '#9333ea', '#ea580c',
    '#0891b2', '#be185d', '#4f46e5', '#ca8a04', '#059669',
    '#7c3aed', '#e11d48', '#0d9488', '#d97706', '#6366f1'
  ];

  // Layout: 3 columns (blue left, yellow middle, gold right)
  const nodeR = 18;
  const depR = 24;
  const vertGap = Math.max(60, Math.min(85, 480 / n || 60));
  const totalH = n * vertGap;
  const corrZoneW = 90;
  const leftX = corrZoneW + 60;
  const midXCol = leftX + 340;
  const rightX = midXCol + 200;
  const svgW = rightX + 110;
  const padTop = 50;
  const svgH = totalH + padTop + 80;
  const depY = padTop + totalH / 2;

  // High-DPI client-side download of the rendered Wright's-method
  // SVG. Hooks must run unconditionally per the rules of hooks, so
  // this useCallback sits above the empty-state early return below.
  const handleDownload = React.useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const SCALE = 2;
    const serializer = new XMLSerializer();
    const clone = svg.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', String(svgW));
    clone.setAttribute('height', String(svgH));
    // Inline a white background rect so the PNG isn't transparent
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('x', '0');
    bg.setAttribute('y', '0');
    bg.setAttribute('width', String(svgW));
    bg.setAttribute('height', String(svgH));
    bg.setAttribute('fill', '#FAFCFB');
    clone.insertBefore(bg, clone.firstChild);
    const xml = serializer.serializeToString(clone);
    const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = svgW * SCALE;
      canvas.height = svgH * SCALE;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FAFCFB';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        const dlUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dlUrl;
        a.download = `path_diagram_${depVar || 'output'}_wrights_method.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(dlUrl);
      }, 'image/png', 1.0);
    };
    img.onerror = () => { URL.revokeObjectURL(url); };
    img.src = url;
  }, [svgW, svgH, depVar]);

  if (n === 0) return null;

  const leftNodes = vars.map((v, i) => ({
    id: i + 1, label: v,
    x: leftX, y: padTop + i * vertGap + vertGap / 2,
    direct: pathCoeffs[v]?.direct_effect || 0,
    totalCorr: pathCoeffs[v]?.total_correlation || 0,
    indirects: pathCoeffs[v]?.indirect_effects || {},
    color: varColors[i % varColors.length],
  }));

  const midNodes = vars.map((v, i) => ({
    id: n + i + 1, label: v,
    x: midXCol, y: padTop + i * vertGap + vertGap / 2,
    direct: pathCoeffs[v]?.direct_effect || 0,
    color: '#eab308',
  }));

  const shortLabel = (s, max = 14) => s.length > max ? s.slice(0, max - 1) + '\u2026' : s;

  return (
    <div className="bg-gradient-to-br from-[#FAFCFB] to-[#F0F4F1] rounded-2xl border border-[#D5E0D8] p-4 mt-2" data-testid="path-diagram">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-[#2C2A28] text-sm uppercase tracking-wider flex items-center gap-2">
          <GitFork size={16} className="text-[#E67E22]" /> Path Diagram (Wright's Method)
        </h4>
        <button
          type="button"
          onClick={handleDownload}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#6B8E7B] text-white rounded-lg hover:bg-[#5A7B6A] transition-colors font-medium shadow-sm"
          data-testid="path-diagram-wrights-download"
          title="Download this diagram as a high-resolution PNG"
        >
          <Download size={13} />
          Download PNG
        </button>
      </div>
      <svg ref={svgRef} width="100%" viewBox={`0 0 ${svgW} ${svgH}`} className="select-none" style={{ minHeight: Math.max(380, svgH) }}>
        <defs>
          {varColors.slice(0, n).map((c, i) => (
            <marker key={`m${i}`} id={`arr${i}`} markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
              <polygon points="0 0, 7 2.5, 0 5" fill={c} />
            </marker>
          ))}
          <marker id="arrYellow" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
            <polygon points="0 0, 7 2.5, 0 5" fill="#ca8a04" />
          </marker>
          <marker id="arrGray" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
            <polygon points="0 0, 7 2.5, 0 5" fill="#9ca3af" />
          </marker>
          <filter id="nShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.10" />
          </filter>
        </defs>

        {/* ===== CORRELATION ARCS (far left, between blue circles) ===== */}
        {correlationMatrix && leftNodes.map((n1, i) =>
          leftNodes.slice(i + 1).map((n2) => {
            const j = vars.indexOf(n2.label);
            const r = correlationMatrix[n1.label]?.[n2.label] ?? 0;
            if (Math.abs(r) < 0.01) return null;
            const y1 = n1.y; const y2 = n2.y;
            const dist = j - i;
            const cx = leftX - nodeR - 14 - dist * 14;
            const isActive = !hovered || hovered === n1.label || hovered === n2.label;
            return (
              <g key={`corr-${n1.label}-${n2.label}`} opacity={isActive ? 0.8 : 0.08}>
                <path
                  d={`M ${leftX - nodeR} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${leftX - nodeR} ${y2}`}
                  fill="none" stroke={r > 0 ? '#4b5563' : '#ef4444'}
                  strokeWidth={Math.max(0.6, Math.abs(r) * 2)}
                />
                <polygon points={`${leftX - nodeR} ${y1 - 3}, ${leftX - nodeR + 4} ${y1}, ${leftX - nodeR} ${y1 + 3}`} fill={r > 0 ? '#4b5563' : '#ef4444'} />
                <polygon points={`${leftX - nodeR} ${y2 - 3}, ${leftX - nodeR + 4} ${y2}, ${leftX - nodeR} ${y2 + 3}`} fill={r > 0 ? '#4b5563' : '#ef4444'} />
                <text x={cx - 2} y={(y1 + y2) / 2 + 3} textAnchor="end" fontSize="8" fill="#374151" fontFamily="monospace" fontWeight="600">
                  r{n1.id}{n2.id}={r.toFixed(3)}
                </text>
              </g>
            );
          })
        )}

        {/* ===== ARROWS: Left (Blue) -> Middle (Yellow) ===== */}
        {leftNodes.map((lNode, i) => {
          const isActive = !hovered || hovered === lNode.label;
          return midNodes.map((mNode, j) => {
            let val;
            const isSelf = i === j;
            if (isSelf) {
              val = lNode.direct;
            } else {
              val = correlationMatrix?.[lNode.label]?.[mNode.label] ?? 0;
            }
            if (Math.abs(val) < 0.001 && !isSelf) return null;
            const sw = isSelf ? Math.max(1.5, Math.abs(val) * 3.5) : Math.max(0.5, Math.abs(val) * 1.5);
            const yOff = (i - j) * 2;
            return (
              <g key={`lm-${i}-${j}`} opacity={isActive ? (isSelf ? 1 : 0.55) : 0.05}>
                <line
                  x1={lNode.x + nodeR} y1={lNode.y}
                  x2={mNode.x - nodeR} y2={mNode.y + yOff}
                  stroke={lNode.color} strokeWidth={sw}
                  markerEnd={`url(#arr${i})`}
                  strokeOpacity={isSelf ? 1 : 0.5}
                />
                {(isSelf || (isActive && Math.abs(val) > 0.1)) && (
                  <text
                    x={(lNode.x + mNode.x) / 2}
                    y={(lNode.y + mNode.y + yOff) / 2 + (i <= j ? -4 : 8)}
                    textAnchor="middle"
                    fontSize={isSelf ? '9' : '7'}
                    fontFamily="monospace"
                    fontWeight={isSelf ? '700' : '600'}
                    fill={lNode.color}
                  >
                    {isSelf ? `P${lNode.id}=${val.toFixed(3)}` : `r${lNode.id}${j + 1}=${val.toFixed(3)}`}
                  </text>
                )}
              </g>
            );
          });
        })}

        {/* ===== CORRELATION ARCS between Middle (Yellow) nodes ===== */}
        {correlationMatrix && midNodes.map((m1, i) =>
          midNodes.slice(i + 1).map((m2, offset) => {
            const j = i + 1 + offset;
            const r = correlationMatrix[m1.label]?.[m2.label] ?? 0;
            if (Math.abs(r) < 0.3 || j - i > 1) return null;
            const y1 = m1.y; const y2 = m2.y;
            const cx2 = midXCol + nodeR + 12 + Math.abs(r) * 20;
            return (
              <g key={`midcorr-${i}-${j}`} opacity={0.4}>
                <path
                  d={`M ${midXCol + nodeR} ${y1} C ${cx2} ${y1}, ${cx2} ${y2}, ${midXCol + nodeR} ${y2}`}
                  fill="none" stroke="#6b7280" strokeWidth="0.8"
                />
                <text x={cx2 + 2} y={(y1 + y2) / 2 + 3} fontSize="7" fill="#6b7280" fontFamily="monospace" fontWeight="600">
                  r{i + 1}{j + 1}={r.toFixed(3)}
                </text>
              </g>
            );
          })
        )}

        {/* ===== ARROWS: Middle (Yellow) -> Right (Dependent Y) ===== */}
        {midNodes.map((mNode, j) => {
          const d = mNode.direct;
          const sw = Math.max(1, Math.min(4, Math.abs(d) * 5));
          const isActive = !hovered || hovered === mNode.label;
          return (
            <g key={`my-${j}`} opacity={isActive ? 1 : 0.1}>
              <line
                x1={mNode.x + nodeR} y1={mNode.y}
                x2={rightX - depR - 2} y2={depY}
                stroke="#ca8a04" strokeWidth={sw}
                markerEnd="url(#arrYellow)"
              />
              <text
                x={(mNode.x + nodeR + rightX - depR) / 2 + (mNode.y < depY ? 8 : -8)}
                y={(mNode.y + depY) / 2 + (mNode.y < depY ? -3 : 8)}
                textAnchor="middle" fontSize="9" fontFamily="monospace" fontWeight="700" fill="#92400e"
              >
                P{j + 1},{n + 1}={d.toFixed(3)}
              </text>
            </g>
          );
        })}

        {/* ===== RESIDUAL ARROW ===== */}
        <g opacity={hovered ? 0.2 : 0.7}>
          <line x1={rightX + 10} y1={depY - 50} x2={rightX + 10} y2={depY - depR - 2}
            stroke="#9ca3af" strokeWidth="1.5" strokeDasharray="4,3" markerEnd="url(#arrGray)" />
          <text x={rightX + 10} y={depY - 55} textAnchor="middle" fontSize="9" fontFamily="monospace" fontWeight="700" fill="#6b7280">
            Residual e={residualEffect?.toFixed(3) || '0'}
          </text>
        </g>

        {/* ===== LEFT NODES (Blue circles with diagonal labels) ===== */}
        {leftNodes.map((node) => {
          const isActive = !hovered || hovered === node.label;
          return (
            <g key={`ln-${node.label}`}
              onMouseEnter={() => setHovered(node.label)}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: 'pointer' }}
              opacity={isActive ? 1 : 0.15}
            >
              <circle cx={node.x} cy={node.y} r={nodeR} fill="#3b82f6" stroke="#1e40af" strokeWidth="2" filter="url(#nShadow)" />
              <text x={node.x} y={node.y + 5} textAnchor="middle" fontSize="13" fontWeight="800" fill="white" fontFamily="system-ui">{node.id}</text>
              <text
                x={node.x - nodeR - 4} y={node.y + 4}
                textAnchor="end" fontSize="9" fontWeight="600" fill="#1e293b" fontFamily="system-ui"
                transform={`rotate(-35, ${node.x - nodeR - 4}, ${node.y + 4})`}
              >
                {shortLabel(node.label, 18)}
              </text>
            </g>
          );
        })}

        {/* ===== MIDDLE NODES (Yellow circles with labels) ===== */}
        {midNodes.map((node, j) => {
          const isActive = !hovered || hovered === node.label;
          return (
            <g key={`mn-${node.label}`}
              onMouseEnter={() => setHovered(node.label)}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: 'pointer' }}
              opacity={isActive ? 1 : 0.15}
            >
              <circle cx={node.x} cy={node.y} r={nodeR} fill="#facc15" stroke="#ca8a04" strokeWidth="2" filter="url(#nShadow)" />
              <text x={node.x} y={node.y + 5} textAnchor="middle" fontSize="12" fontWeight="800" fill="#422006" fontFamily="system-ui">{j + 1}</text>
              <text x={node.x} y={node.y - nodeR - 4} textAnchor="middle" fontSize="8" fontWeight="600" fill="#422006" fontFamily="system-ui">
                {shortLabel(node.label, 14)}
              </text>
            </g>
          );
        })}

        {/* ===== DEPENDENT VARIABLE NODE (Gold/amber) ===== */}
        <g>
          <circle cx={rightX} cy={depY} r={depR} fill="#f59e0b" stroke="#b45309" strokeWidth="2.5" filter="url(#nShadow)" />
          <text x={rightX} y={depY - 2} textAnchor="middle" fontSize="14" fontWeight="800" fill="white" fontFamily="system-ui">{n + 1}</text>
          <text x={rightX} y={depY + 11} textAnchor="middle" fontSize="7" fill="#FFF7ED" fontFamily="monospace">(Y)</text>
          <text x={rightX + depR + 8} y={depY} textAnchor="start" fontSize="10" fontWeight="700" fill="#1e293b" fontFamily="system-ui"
            transform={`rotate(90, ${rightX + depR + 8}, ${depY})`}
          >
            {shortLabel(depVar, 18)}
          </text>
          <text x={rightX} y={depY + depR + 15} textAnchor="middle" fontSize="10" fontFamily="monospace" fontWeight="700" fill="#1e293b">
            R²={rSquared?.toFixed(4) || '0'}
          </text>
        </g>

        {/* ===== LEGEND ===== */}
        <g transform={`translate(8, ${svgH - 42})`}>
          <rect width={svgW - 16} height="34" rx="5" fill="#F9F8F6" stroke="#E8E6E1" />
          <circle cx="16" cy="17" r="6" fill="#3b82f6" />
          <text x="28" y="21" fontSize="9" fill="#555" fontFamily="system-ui">Xi (Cause)</text>
          <circle cx="110" cy="17" r="6" fill="#facc15" stroke="#ca8a04" strokeWidth="1" />
          <text x="122" y="21" fontSize="9" fill="#555" fontFamily="system-ui">Xi (Component)</text>
          <circle cx="230" cy="17" r="6" fill="#f59e0b" />
          <text x="242" y="21" fontSize="9" fill="#555" fontFamily="system-ui">Y (Dep)</text>
          {varColors.slice(0, Math.min(n, 5)).map((c, i) => (
            <React.Fragment key={`leg${i}`}>
              <line x1={310 + i * 55} y1="17" x2={325 + i * 55} y2="17" stroke={c} strokeWidth="2" />
              <text x={330 + i * 55} y="21" fontSize="8" fill="#555" fontFamily="monospace">X{i + 1}</text>
            </React.Fragment>
          ))}
          <text x={svgW - 50} y="21" fontSize="8" fill="#aaa" fontFamily="monospace">Hover Xi</text>
        </g>
      </svg>
    </div>
  );
}

/* ============================================================
   PATH ANALYSIS VIEW (proper direct/indirect table + diagram)
============================================================ */


export function PathAnalysisView({ data }) {
  if (!data) return null;
  const pathCoeffs = data.path_coefficients || {};
  const vars = Object.keys(pathCoeffs);
  const depVar = data.dependent_variable || 'Y';

  return (
    <div className="space-y-6" data-testid="path-analysis-view">
      {data.interpretation && (
        <div className="bg-[#EAF0EC] rounded-2xl p-5">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#F9F8F6] rounded-2xl p-5 text-center border border-[#E8E6E1]">
          <p className="text-xs text-[#8A8580] uppercase tracking-wider mb-2">Dependent Variable</p>
          <p className="font-mono text-lg font-bold text-[#6B8E7B]">{depVar}</p>
        </div>
        <div className="bg-[#F9F8F6] rounded-2xl p-5 text-center border border-[#E8E6E1]">
          <p className="text-xs text-[#8A8580] uppercase tracking-wider mb-2">R²</p>
          <p className="font-mono text-2xl font-bold text-[#2C2A28]">{data.r_squared?.toFixed(4) || '-'}</p>
        </div>
        <div className="bg-[#F9F8F6] rounded-2xl p-5 text-center border border-[#E8E6E1]">
          <p className="text-xs text-[#8A8580] uppercase tracking-wider mb-2">Residual Effect</p>
          <p className="font-mono text-2xl font-bold text-[#C87A5E]">{data.residual_effect?.toFixed(4) || '-'}</p>
        </div>
      </div>

      {/* PATH DIAGRAM */}
      <PathDiagram
        pathCoeffs={pathCoeffs}
        depVar={depVar}
        correlationMatrix={data.correlation_matrix}
        residualEffect={data.residual_effect}
        rSquared={data.r_squared}
      />

      {/* PUBLICATION-QUALITY DIAGRAM DOWNLOADS (P2 Niranjan, Feb 2026) */}
      {(data.path_diagram_svg || data.chart) && (
        <div
          className="flex flex-wrap items-center gap-3 px-5 py-3 bg-[#F9F8F6] border border-[#E8E6E1] rounded-2xl"
          data-testid="path-diagram-download-bar"
        >
          <span className="text-xs text-[#5E5A56] font-medium">
            Download publication-quality diagram:
          </span>
          {data.path_diagram_svg && (
            <button
              type="button"
              onClick={() => {
                const blob = new Blob([data.path_diagram_svg], { type: 'image/svg+xml' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `path_diagram_${depVar || 'output'}.svg`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              className="text-xs px-3 py-1.5 bg-[#6B8E7B] text-white rounded-lg hover:bg-[#5A7B6A] transition-colors font-medium"
              data-testid="path-diagram-download-svg"
            >
              SVG (vector)
            </button>
          )}
          {data.chart && (
            <button
              type="button"
              onClick={() => {
                const a = document.createElement('a');
                a.href = `data:image/png;base64,${data.chart}`;
                a.download = `path_diagram_${depVar || 'output'}.png`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }}
              className="text-xs px-3 py-1.5 bg-[#C87A5E] text-white rounded-lg hover:bg-[#A66547] transition-colors font-medium"
              data-testid="path-diagram-download-png"
            >
              PNG (300 DPI)
            </button>
          )}
          <span className="text-xs text-[#8A8580] italic ml-auto">
            Nature-journal layout · same diagram embedded in DOCX export
          </span>
        </div>
      )}

      {/* Top-K truncation note (Sub-Batch 3AB): for high-dim inputs we
          cap the visual at the 10 strongest predictors by |direct
          effect| — the table below still shows all coefficients. */}
      {data.path_diagram_truncated_to_top_k && (
        <div
          className="rounded-xl border border-[#F4E5B5] bg-[#FFFBEB] px-4 py-3 text-[12px] leading-relaxed text-[#7A5A1A]"
          data-testid="path-diagram-truncation-note"
        >
          <span className="font-semibold">Path diagram shows top-10 predictors</span>{' '}
          (ranked by |direct effect|). The complete table of direct &amp;
          indirect effects below includes all{' '}
          {(data.path_diagram_skipped_predictors?.length || 0) + 10}{' '}
          predictors. Predictors omitted from the visual:{' '}
          <span className="font-mono">
            {(data.path_diagram_skipped_predictors || []).join(', ')}
          </span>
          .
        </div>
      )}

      {/* Direct & Indirect Effects Table */}
      {vars.length > 0 && (
        <div className="overflow-x-auto">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Direct & Indirect Effects on {depVar}</h4>
          <table className="w-full text-sm border-collapse" data-testid="path-effects-table">
            <thead>
              <tr className="bg-[#EAF0EC]">
                <th className="border border-[#D5D1CC] px-3 py-2.5 text-left font-semibold text-xs uppercase sticky left-0 bg-[#EAF0EC] z-10">Character</th>
                <th className="border border-[#D5D1CC] px-3 py-2.5 text-center font-semibold text-xs uppercase bg-[#D4E6D8]">Direct Effect</th>
                {vars.map(v => (
                  <th key={v} className="border border-[#D5D1CC] px-3 py-2.5 text-center font-semibold text-xs">Indirect via {v}</th>
                ))}
                <th className="border border-[#D5D1CC] px-3 py-2.5 text-center font-semibold text-xs uppercase bg-[#FDF6F0]">Total Indirect</th>
                <th className="border border-[#D5D1CC] px-3 py-2.5 text-center font-semibold text-xs uppercase bg-[#EAF0EC]">Total Corr (r)</th>
              </tr>
            </thead>
            <tbody>
              {vars.map(variable => {
                const coeff = pathCoeffs[variable] || {};
                const directEffect = coeff.direct_effect || 0;
                const indirects = coeff.indirect_effects || {};

                return (
                  <tr key={variable} className="hover:bg-[#F5F4F2]">
                    <td className="border border-[#E8E6E1] px-3 py-2 font-semibold text-[#2C2A28] text-xs sticky left-0 bg-white z-10">{variable}</td>
                    <td className={`border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs font-bold ${
                      directEffect > 0 ? 'text-emerald-600 bg-emerald-50' : directEffect < 0 ? 'text-red-600 bg-red-50' : 'text-[#5E5A56]'
                    }`}>
                      {directEffect.toFixed(4)}
                    </td>
                    {vars.map(via => (
                      <td key={via} className="border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs text-[#5E5A56]">
                        {variable === via ? (
                          <span className="text-[#C0C0C0]">—</span>
                        ) : (
                          (indirects[`via_${via}`] || 0).toFixed(4)
                        )}
                      </td>
                    ))}
                    <td className="border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs font-semibold text-[#C87A5E] bg-[#FDF6F0]">
                      {(coeff.total_indirect || 0).toFixed(4)}
                    </td>
                    <td className="border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs font-bold text-[#2C2A28] bg-[#EAF0EC]">
                      {(coeff.total_correlation || 0).toFixed(4)}
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-[#F9F8F6] font-bold">
                <td className="border border-[#D5D1CC] px-3 py-2 text-xs sticky left-0 bg-[#F9F8F6] z-10">Residual Effect</td>
                <td colSpan={vars.length + 3} className="border border-[#D5D1CC] px-3 py-2 text-center font-mono text-xs text-[#C87A5E]">
                  {data.residual_effect?.toFixed(4) || '-'}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="text-xs text-[#8A8580] mt-2">Bold green/red = direct effects | Diagonal = self (excluded) | Residual = unexplained variation</p>
        </div>
      )}

      {/* Correlation Matrix */}
      {data.correlation_matrix && (
        <div className="overflow-x-auto mt-6">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Inter-Character Correlation Matrix</h4>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#EAF0EC]">
                <th className="border border-[#D5D1CC] px-3 py-2.5 text-left font-semibold text-xs sticky left-0 bg-[#EAF0EC] z-10"></th>
                {Object.keys(data.correlation_matrix).map(c => (
                  <th key={c} className="border border-[#D5D1CC] px-3 py-2.5 text-center font-semibold text-xs">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.correlation_matrix).map(([row, cols]) => (
                <tr key={row}>
                  <td className="border border-[#E8E6E1] px-3 py-2 font-semibold text-xs bg-[#F5F4F2] sticky left-0 z-10">{row}</td>
                  {Object.entries(cols).map(([col, val]) => {
                    const num = typeof val === 'number' ? val : parseFloat(val);
                    const bg = row === col ? 'bg-[#F5F4F2]' : Math.abs(num) > 0.7 ? 'bg-emerald-50' : '';
                    return (
                      <td key={col} className={`border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs ${bg}`}>
                        {!isNaN(num) ? num.toFixed(3) : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.chart && <img src={`data:image/png;base64,${data.chart}`} alt="Path Analysis Chart" className="w-full rounded-2xl border border-[#E8E6E1] mt-4" data-testid="path-chart" />}
    </div>
  );
}

/* ============================================================
   PCA VIEW (proper eigenvalue table + loadings table)
============================================================ */
